/**
 * flag_control_gaps — safe agent tool. Calls compliance evaluator and
 * returns the controls that are not compliant. Like compliance_gaps
 * but always returns a flat list focused on what's not yet covered.
 */

import type { IAgentTool, IAgentToolContext, AgentToolResult } from '../../../../core/ai/agent/IAgentTool';

function getFolderId(context: IAgentToolContext): string | null {
  const ctx = context.assistantContext as Record<string, unknown> | undefined;
  const f = ctx?.folderId;
  return typeof f === 'string' && f.length > 0 ? f : null;
}

interface ControlRow {
  controlId: string;
  controlLabel: string;
  status: 'compliant' | 'warning' | 'violation' | 'unknown';
  reason: string;
  evidenceNodeIds: string[];
  processNodeIds: string[];
}

export const flagControlGapsTool: IAgentTool = {
  name: 'flag_control_gaps',
  description:
    'List controls in the current folder + framework that are not yet compliant — i.e. status is violation, warning, or unknown. Use when the user asks where they are missing evidence or which controls still need work.',
  parameters: {
    type: 'object',
    properties: {
      framework: { type: 'string', description: 'Framework id (default soc2)' },
      includeUnknown: {
        type: 'boolean',
        description: "Default true. When false, only returns 'violation' + 'warning'.",
      },
    },
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'query',

  async execute(params, context): Promise<AgentToolResult> {
    const folderId = getFolderId(context);
    if (!folderId) return { success: false, summary: 'No folder context available.' };
    const framework = typeof params.framework === 'string' && params.framework ? params.framework : 'soc2';
    const includeUnknown = params.includeUnknown !== false;

    const url = `/api/compass/folders/${encodeURIComponent(folderId)}/compliance/${encodeURIComponent(framework)}/report`;
    const response = await fetch(url);
    if (!response.ok) return { success: false, summary: `Report request failed (${response.status})` };
    const body = (await response.json()) as { controls: ControlRow[] };
    const failing = body.controls.filter((c) => {
      if (c.status === 'compliant') return false;
      if (!includeUnknown && c.status === 'unknown') return false;
      return true;
    });

    if (failing.length === 0) {
      return {
        success: true,
        summary: `No control gaps for ${framework} — all controls compliant.`,
        data: { controls: [] },
      };
    }

    const grouped = {
      violation: failing.filter((c) => c.status === 'violation'),
      warning: failing.filter((c) => c.status === 'warning'),
      unknown: failing.filter((c) => c.status === 'unknown'),
    };
    const sections: string[] = [];
    if (grouped.violation.length > 0) {
      sections.push(
        `Violations (${grouped.violation.length}):\n` +
          grouped.violation.slice(0, 6).map((c) => `- ${c.controlId}: ${c.reason}`).join('\n'),
      );
    }
    if (grouped.warning.length > 0) {
      sections.push(
        `Warnings (${grouped.warning.length}):\n` +
          grouped.warning.slice(0, 6).map((c) => `- ${c.controlId}: ${c.reason}`).join('\n'),
      );
    }
    if (includeUnknown && grouped.unknown.length > 0) {
      sections.push(
        `Unknown (${grouped.unknown.length}):\n` +
          grouped.unknown.slice(0, 6).map((c) => `- ${c.controlId}: ${c.reason}`).join('\n'),
      );
    }

    return {
      success: true,
      summary: `${framework} has ${failing.length} non-compliant control(s).\n\n${sections.join('\n\n')}`,
      data: { controls: failing },
      uiSurface: 'chat',
    };
  },
};
